import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStore } from "store/zustand";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { getAuth } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import Select from "react-select";
import {
  fetchAirlinesApi,
  fetchHomeBasesApi,
  updateAccountApi,
  uploadFileApi,
  validateReferralCodeApi,
  verifyUploadApi,
} from "apis/authServices";
import { auth, firestore } from "config/firebaseConfig";
import TermsModal from "components/TermsModal";

interface Airline {
  id: number;
  airlineName: string;
  logo: string | null;
}

interface Airport {
  id: number;
  name: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  employerAirlineId: string;
  employerAirline: string;
  homebaseId: string;
  homebase: string;
  referralCode: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const CreateAccount: React.FC = () => {
  const navigate = useNavigate();
  const { setIsAuthenticated } = useStore();
  const [searchParams] = useSearchParams();
  const { showLoader, hideLoader } = useGlobalLoader();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [airlines, setAirlines] = useState<Airline[]>([]);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [isLoadingAirlines, setIsLoadingAirlines] = useState(false);
  const [isLoadingAirports, setIsLoadingAirports] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState<string>("");
  const [uploadVerificationMessage, setUploadVerificationMessage] = useState<string>("");
  const [referralCodeError, setReferralCodeError] = useState<string>("");

  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    employerAirlineId: "",
    employerAirline: "",
    homebaseId: "",
    homebase: "",
    referralCode: "",
  });
  useEffect(() => {
    if (formData.referralCode === "") {
      setReferralCodeError("");
    }
  }, [formData.referralCode]);
  // Auto-fill referral code from query parameter
  useEffect(() => {
    const referralCodeFromUrl = searchParams.get("referral");
    if (referralCodeFromUrl && !formData.referralCode) {
      setFormData((prev) => ({ ...prev, referralCode: referralCodeFromUrl }));
      validateReferralCodeApi(referralCodeFromUrl)
        .then(() => setReferralCodeError(""))
        .catch(() => setReferralCodeError("Invalid referral code "));
    }
  }, [searchParams]);

  // Fetch airlines and home bases on component mount
  useEffect(() => {
    // Load initial limited data
    fetchAirlines("", 50); // Limit initial load to 50 items
    fetchHomeBases("", 50); // Limit initial load to 50 items
  }, []);

  // Debounce function for search input
  const debounce = useCallback((func: (...args: any[]) => void, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }, []);

  const fetchAirlines = async (query = "", limit = 100) => {
    try {
      setIsLoadingAirlines(true);
      const response = await fetchAirlinesApi(query);
      const airlineData = Array.isArray(response) ? response : [];

      // Limit the number of items to improve performance
      const limitedData = airlineData.slice(0, limit);
      setAirlines(limitedData);
    } catch (error) {
      console.error("Error fetching airlines:", error);
      setAirlines([]);
    } finally {
      setIsLoadingAirlines(false);
    }
  };

  const fetchHomeBases = async (query = "", limit = 100) => {
    try {
      setIsLoadingAirports(true);
      const response = await fetchHomeBasesApi(query);
      const airportData = Array.isArray(response) ? response : [];

      // Limit the number of items to improve performance
      const limitedData = airportData.slice(0, limit);
      setAirports(limitedData);
    } catch (error) {
      console.error("Error fetching home bases:", error);
      setAirports([]);
    } finally {
      setIsLoadingAirports(false);
    }
  };

  // Create debounced search functions
  const debouncedFetchAirlines = useCallback(
    debounce((query: string) => fetchAirlines(query, 100), 300),
    [debounce]
  );

  const debouncedFetchHomeBases = useCallback(
    debounce((query: string) => fetchHomeBases(query, 100), 300),
    [debounce]
  );

  const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "referralCode" && formData.referralCode) {
      setReferralCodeError("");
      if (value && value.length > 1) {
        console.log(`Validating referral code: ${value}`);

        try {
          await validateReferralCodeApi(value);
          setReferralCodeError("");
        } catch (error) {
          setReferralCodeError("Invalid referral code");
        }
      }
    }
  };

  const handleSelectAirline = (selectedOption: any) => {
    setFormData((prev) => ({
      ...prev,
      employerAirlineId: selectedOption ? selectedOption.value : "",
      employerAirline: selectedOption ? selectedOption.label : "",
    }));
  };

  const handleSelectHomeBase = (selectedOption: any) => {
    setFormData((prev) => ({
      ...prev,
      homebaseId: selectedOption ? selectedOption.value : "",
      homebase: selectedOption ? selectedOption.label : "",
    }));
  };

  const validateFile = (file: File): boolean => {
    const allowedFormats = ["image/jpeg"];
    if (!allowedFormats.includes(file.type)) {
      setUploadVerificationMessage("Only JPEG/JPG files are allowed.");
      return false;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setVerificationMessage("Please upload an image that is 2 MB or smaller.");
      return false;
    }

    return true;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const file = event.target.files?.[0] || null;
    if (file && validateFile(file)) {
      setSelectedFile(file);
      setUploadVerificationMessage("");
    } else {
      setSelectedFile(null);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
      setUploadVerificationMessage("");
    }
  };

  const fetchVerificationStatusFromFirestore = (userId: string): Promise<{ message: string; isAirlineStaff: boolean }> => {
    return new Promise((resolve, reject) => {
      const MAX_TIMEOUT = 30000;
      let elapsedTime = 0;
      const INTERVAL = 2000;

      console.log(`[Firestore Debug] Starting fetch for userId: ${userId} at ${new Date().toISOString()}`);

      const tryFetch = () => {
        const userDocRef = doc(firestore, "imageProcessingStatus", userId);
        const unsubscribe = onSnapshot(
          userDocRef,
          (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data();
              console.log(`[Firestore Debug] Fetched document from imageProcessingStatus/${userId}:`, {
                description: data?.description,
                queueStatus: data?.queueStatus,
                isAirlineStaff: data?.isAirlineStaff,
                updatedAt: data?.updatedAt?.toDate().toISOString(),
              });

              const queueStatus = data?.queueStatus;
              const isAirlineStaff = data?.isAirlineStaff;

              if (queueStatus === "completed" && typeof isAirlineStaff === "boolean") {
                console.log(`[Firestore Debug] Verification complete - queueStatus: ${queueStatus}, isAirlineStaff: ${isAirlineStaff}`);
                clearTimeout(timeoutId);
                clearInterval(intervalId);
                unsubscribe();
                resolve({
                  message: data?.description || "Verification status received.",
                  isAirlineStaff,
                });
              } else {
                console.log(`[Firestore Debug] Waiting for verification - queueStatus: ${queueStatus}, isAirlineStaff: ${isAirlineStaff}`);
              }
            } else if (elapsedTime >= MAX_TIMEOUT) {
              console.log(`[Firestore Debug] Document imageProcessingStatus/${userId} not found after ${elapsedTime}ms.`);
              clearTimeout(timeoutId);
              clearInterval(intervalId);
              unsubscribe();
              reject(new Error("Verification record not found."));
            } else {
              console.log(
                `[Firestore Debug] Document imageProcessingStatus/${userId} not found yet. Elapsed time: ${elapsedTime}ms. Retrying...`
              );
            }
          },
          (error) => {
            console.log(`[Firestore Debug] Firestore error: ${error.message}`);
            clearTimeout(timeoutId);
            clearInterval(intervalId);
            unsubscribe();
            reject(new Error(`Firestore error: ${error.message}`));
          }
        );
      };

      const timeoutId = setTimeout(() => {
        console.log(`[Firestore Debug] Verification timed out after ${MAX_TIMEOUT}ms.`);
        reject(new Error("Verification timed out. Please try again."));
      }, MAX_TIMEOUT);

      tryFetch();
      const intervalId = setInterval(() => {
        elapsedTime += INTERVAL;
        if (elapsedTime < MAX_TIMEOUT) {
          console.log(`[Firestore Debug] Retrying fetch at ${elapsedTime}ms...`);
          tryFetch();
        } else {
          clearInterval(intervalId);
        }
      }, INTERVAL);
    });
  };

  const handleSubmit = async () => {
    if (!formData.firstName.trim()) {
      setVerificationMessage("Please enter your first name.");
      return;
    }

    if (!formData.lastName.trim()) {
      setVerificationMessage("Please enter your last name.");
      return;
    }

    if (!formData.employerAirlineId) {
      setVerificationMessage("Please select your employer airline.");
      return;
    }

    if (!formData.homebaseId) {
      setVerificationMessage("Please select your home base.");
      return;
    }

    if (!selectedFile) {
      setVerificationMessage("Please upload your proof of employment.");
      return;
    }

    if (!termsAccepted) {
      setVerificationMessage("Terms and conditions must be accepted.");
      return;
    }

    if (referralCodeError) {
      setVerificationMessage("Please enter a valid referral code or leave it empty.");
      return;
    }

    setIsLoading(true);
    setVerificationMessage("");
    showLoader();
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated. Please log in.");
      }
      const uid = user.uid;
      console.log("UserId: " + uid);

      if (selectedFile) {
        const uploadResponse = await uploadFileApi(selectedFile);
        if (!uploadResponse || typeof uploadResponse !== "object") {
          throw new Error("Invalid or undefined upload response");
        }
        console.log("Upload response: ", uploadResponse);

        const { fileUrl, userId, message: uploadMessage } = uploadResponse;
        if (fileUrl === undefined || fileUrl === null || userId === undefined || userId === null) {
          throw new Error("Missing fileUrl or userId in upload response");
        }

        setVerificationMessage(uploadResponse.message);

        const verifyResponse = await verifyUploadApi(uploadResponse.fileUrl, uploadResponse.userId);

        if (!verifyResponse?.message) {
          throw new Error("Invalid verification response");
        }

        const { message, isAirlineStaff } = await fetchVerificationStatusFromFirestore(uid);

        if (isAirlineStaff === false) {
          throw new Error("Cannot verify your image, try another image.");
        }

        if (isAirlineStaff !== true) {
          throw new Error("Unexpected verification status. Please try again.");
        }

        setVerificationMessage(message);
      }

      await updateAccountApi(
        formData.firstName,
        formData.lastName,
        Number(formData.employerAirlineId),
        Number(formData.homebaseId),
        formData.referralCode
      );

      setIsAuthenticated(true);
      navigate("/");
    } catch (error: any) {
      setVerificationMessage(error.message || "Image verification failed. Please try again.");
    } finally {
      hideLoader();
      setIsLoading(false);
    }
  };

  // Transform airlines and airports into react-select options with memoization
  const airlineOptions = useMemo(
    () =>
      airlines.map((airline) => ({
        value: airline.id.toString(),
        label: airline.airlineName,
      })),
    [airlines]
  );

  const airportOptions = useMemo(
    () =>
      airports.map((airport) => ({
        value: airport.id.toString(),
        label: airport.name,
      })),
    [airports]
  );

  // Find selected values for react-select with fallback (memoized)
  const selectedAirline = useMemo(
    () =>
      formData.employerAirlineId
        ? airlineOptions.find((option) => option.value === formData.employerAirlineId) || {
            value: formData.employerAirlineId,
            label: formData.employerAirline || formData.employerAirlineId,
          }
        : null,
    [formData.employerAirlineId, formData.employerAirline, airlineOptions]
  );

  const selectedHomeBase = useMemo(
    () =>
      formData.homebaseId
        ? airportOptions.find((option) => option.value === formData.homebaseId) || {
            value: formData.homebaseId,
            label: formData.homebase || formData.homebaseId,
          }
        : null,
    [formData.homebaseId, formData.homebase, airportOptions]
  );

  return (
    <div className="lg:col-span-3 col-span-12 lg:col-start-9 p-4">
      <img className="w-40" src="/images/logo.svg" alt="Logo" />
      <h1 className="text-2xl mt-8 font-semibold text-gray-800">Create an account</h1>
      {verificationMessage && <p className="mt-4 text-sm text-red-700 bg-red-50 p-3 rounded-lg">{verificationMessage}</p>}
      <div className="mt-8 flex items-center gap-4">
        <div className="w-full relative">
          {/* Fake placeholder with red star */}
          {!formData.firstName && (
            <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-sm">
              First Name <span className="text-red-500">*</span>
            </span>
          )}

          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            id="firstName"
            className="block w-full p-4 rounded-lg bg-gray-100 text-sm text-gray-800 
               focus:outline-none focus:ring-2 focus:ring-light-yellow 
               border border-transparent focus:border-light-yellow"
          />
        </div>
        <div className="relative w-full">
          {!formData.lastName && (
            <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-sm">
              Last Name <span className="text-red-500">*</span>
            </span>
          )}

          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            id="lastName"
            className="block w-full p-4 rounded-lg bg-gray-100 text-sm text-gray-800 
               focus:outline-none focus:ring-2 focus:ring-light-yellow 
               border border-transparent focus:border-light-yellow"
          />
        </div>
      </div>
      <div className="mt-4 space-y-4">
        <div className="relative">
          <Select
            className="basic-single"
            classNamePrefix="select"
            value={selectedAirline}
            isLoading={isLoadingAirlines}
            isClearable={true}
            isSearchable={true}
            name="employerAirlineId"
            options={airlineOptions}
            onChange={handleSelectAirline}
            onInputChange={(inputValue, { action }) => {
              if (action === "input-change" && inputValue.trim().length > 1) {
                debouncedFetchAirlines(inputValue);
              }
            }}
            placeholder={
              <span>
                Select Employer Airline <span style={{ color: "red" }}>*</span>
              </span>
            }
            noOptionsMessage={({ inputValue }) =>
              isLoadingAirlines ? "Loading..." : inputValue ? `No airlines found for "${inputValue}"` : "Type to search airlines"
            }
            maxMenuHeight={200}
            styles={{
              control: (base, state) => ({
                ...base,
                outline: "none !important",
                backgroundColor: "#f1f5f9",
                border: state.isFocused ? `1px solid #FFD700` : "1px solid transparent",
                padding: "0.5rem",
                borderRadius: "0.5rem",
                boxShadow: state.isFocused ? `0 0 0 2px #FFD700` : "none",
                "&:hover": {
                  borderColor: state.isFocused ? "#FFD700" : "#CBD5E0",
                },
              }),
              placeholder: (base) => ({
                ...base,
                color: "#A0AEC0",
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
              }),
              singleValue: (base) => ({
                ...base,
                color: "#2D3748",
              }),
              option: (base, state) => ({
                ...base,
                color: "#2D3748", // Option text color (e.g., gray-800)
                // Background when selected or hovered (must contrast with yellow menu background)
                backgroundColor: state.isSelected
                  ? "#FCD34D" // A darker/different yellow for selected
                  : state.isFocused // Background when mouse hovers over option
                    ? "#FDE68A" // A lighter yellow hover
                    : "#FFFFFF", // Default: inherits menu background color
                "&:hover": {
                  backgroundColor: "#FDE68A", // Ensure hover is explicitly set
                },
              }),
            }}
            filterOption={null}
          />

          {airlines.length === 0 && !isLoadingAirlines && <p className="text-sm text-gray-500 mt-1">Start typing to search airlines</p>}
        </div>
        <div className="relative">
          <Select
            className="basic-single"
            classNamePrefix="select"
            value={selectedHomeBase}
            isLoading={isLoadingAirports}
            isClearable={true}
            isSearchable={true}
            name="homebaseId"
            options={airportOptions}
            onChange={handleSelectHomeBase}
            onInputChange={(inputValue, { action }) => {
              if (action === "input-change" && inputValue.trim().length > 1) {
                debouncedFetchHomeBases(inputValue);
              }
            }}
            placeholder={
              <span>
                Select Home Base <span style={{ color: "red" }}>*</span>
              </span>
            }
            noOptionsMessage={({ inputValue }) =>
              isLoadingAirports ? "Loading..." : inputValue ? `No home bases found for "${inputValue}"` : "Type to search home bases"
            }
            maxMenuHeight={200} // Limit menu height
            styles={{
              control: (base, state) => ({
                ...base,
                outline: "none !important",
                backgroundColor: "#f1f5f9",
                border: state.isFocused ? `1px solid #FFD700` : "1px solid transparent",
                padding: "0.5rem",
                borderRadius: "0.5rem",
                boxShadow: state.isFocused ? `0 0 0 2px #FFD700` : "none",
                "&:hover": {
                  borderColor: state.isFocused ? "#FFD700" : "#CBD5E0",
                },
              }),
              placeholder: (base) => ({
                ...base,
                color: "#A0AEC0",
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
              }),
              singleValue: (base) => ({
                ...base,
                color: "#2D3748",
              }),
              option: (base, state) => ({
                ...base,
                color: "#2D3748", // Option text color (e.g., gray-800)
                backgroundColor: state.isSelected
                  ? "#FCD34D" // Selected
                  : state.isFocused
                    ? "#FDE68A" // Hover
                    : "#FFFFFF", // Default
                "&:hover": {
                  backgroundColor: "#FDE68A",
                },
              }),
              input: (base) => ({
                ...base,
                outline: "none !important",
                color: "#2D3748",
                "& input": {
                  outline: "none !important",
                  boxShadow: "none !important",
                  border: "none !important",
                },
              }),
            }}
            filterOption={null} // Disable client-side filtering for better performance
          />
          {airports.length === 0 && !isLoadingAirports && <p className="text-sm text-gray-500 mt-1">Start typing to search home bases</p>}
        </div>
      </div>
      <div className="mt-4">
        <input
          type="text"
          name="referralCode"
          value={formData.referralCode}
          onChange={handleInputChange}
          className="block w-full p-4 rounded-lg bg-gray-100 text-sm text-gray-800 placeholder-gray-400  border border-transparent
          focus:outline-none 
          focus:ring-2 
        focus:ring-light-yellow 
        focus:border-light-yellow"
          placeholder="Referral Code (Optional)"
        />
        {referralCodeError && <p className="mt-2 text-sm text-red-600">{referralCodeError}</p>}
      </div>
      <p className="font-semibold text-sm text-gray-800 mt-6">
        Upload Proof
        <span className="text-red-500 ml-1">*</span>
      </p>
      <div className="flex items-center justify-center w-full mt-4">
        <label
          htmlFor="dropzone-file"
          className={`flex flex-col items-center justify-center w-full h-40 border-2 border-light-yellow border-dashed rounded-lg cursor-pointer hover:bg-blue-50 ${
            isDragging ? "bg-blue-100" : "hover:bg-blue-50"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <img className="w-10" src="/images/upload.svg" alt="Upload" />
            <p className="mb-2 text-xs text-gray-800 mt-3">{selectedFile ? selectedFile.name : "Drag your file(s) to start uploading"}</p>
            <div className="flex items-center gap-4 w-full">
              <div className="w-full h-px bg-gray-200" />
              <p className="text-gray-500 text-xs">OR</p>
              <div className="w-full h-px bg-gray-200" />
            </div>
            <button
              type="button"
              className="font-semibold px-2 py-1 mt-2 rounded-lg text-light-yellow text-center border border-light-yellow text-xs hover:bg-blue-100"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("dropzone-file")?.click();
              }}
            >
              Browse files
            </button>
          </div>
          <input id="dropzone-file" type="file" className="hidden" onChange={handleFileChange} accept="image/jpeg,image/jpg" />
        </label>
      </div>
      <div className="flex items-center mt-6">
        <input
          id="checked-checkbox"
          type="checkbox"
          checked={termsAccepted}
          onClick={(e) => {
            if (!termsAccepted) {
              e.preventDefault();
              setIsTermsModalOpen(true);
            }
          }}
          onChange={(e) => {
            if (termsAccepted) {
              setTermsAccepted(e.target.checked);
            }
          }}
          className="w-4 h-4 text-light-yellow bg-gray-100 border-gray-300 rounded focus:ring-light-yellow cursor-pointer"
        />
        <label htmlFor="checked-checkbox" className="ml-2 text-xs font-medium text-gray-800">
          I accept the{" "}
          <a
            onClick={(e) => {
              e.preventDefault();
              setIsTermsModalOpen(true);
            }}
            className="underline text-light-yellow hover:text-blue-800 cursor-pointer"
          >
            terms & conditions
          </a>
        </label>
      </div>
      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
        onAccept={() => {
          setTermsAccepted(true);
          setIsTermsModalOpen(false);
        }}
      />
      <button
        onClick={handleSubmit}
        disabled={isLoading || !termsAccepted}
        className="w-full p-4 rounded-lg text-white uppercase text-center shadow-md bg-light-yellow hover:bg-light-yellow disabled:bg-light-yellow disabled:cursor-not-allowed text-sm mt-6"
      >
        {isLoading ? "Submitting..." : "Submit"}
      </button>
      {/* {verificationMessage && <p className="mt-4 text-sm text-gray-800 bg-blue-50 p-3 rounded-lg">{verificationMessage}</p>} */}
    </div>
  );
};

export default CreateAccount;
